import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthSession } from "../hooks/useAuthSession";
import { useCampaignQuestions } from "../hooks/useCampaignQuestions";
import { useCampaignAnswers } from "../hooks/useCampaignAnswers";
import QuestionInput from "../components/QuestionInput";
import type { CampaignAnswer } from "../types/CampaignAnswer";

const CampaignForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session, userRole } = useAuthSession();
  const { questions, loading: questionsLoading, error: questionsError } = useCampaignQuestions();
  const { submitAnswers, getExistingAnswers, submitting, error: submitError, success } = useCampaignAnswers(id!, session?.user?.id || null);
  
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Load existing answers when component mounts
  useEffect(() => {
    const loadExistingAnswers = async () => {
      if (!id || !session?.user?.id) return;
      
      setLoadingExisting(true);
      const existingAnswers = await getExistingAnswers();
      
      const answersMap: Record<string, string> = {};
      existingAnswers.forEach((answer: CampaignAnswer) => {
        answersMap[answer.question_id] = answer.answer;
      });
      
      setAnswers(answersMap);
      setLoadingExisting(false);
    };

    loadExistingAnswers();
  }, [id, session?.user?.id, getExistingAnswers]);

  // Handle navigation on successful submission
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        if (userRole === 'csr') {
          navigate('/csr-dashboard');
        } else if (userRole === 'admin') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }, 1500); // Give user time to see success message
      
      return () => clearTimeout(timer);
    }
  }, [success, userRole, navigate]);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[questionId]) {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    questions.forEach(question => {
      if (question.is_required && !answers[question.id]?.trim()) {
        errors[question.id] = "This field is required";
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
      questionId,
      answer
    }));

    await submitAnswers(answersArray);
  };

  const handleBackToHome = () => {
    if (userRole === 'csr') {
      navigate('/csr-dashboard');
    } else if (userRole === 'admin') {
      navigate('/admin');
    } else {
      navigate('/');
    }
  };

  if (questionsLoading || loadingExisting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign questions...</p>
        </div>
      </div>
    );
  }

  if (questionsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-semibold">Error loading questions</p>
            <p className="text-sm">{questionsError.message}</p>
          </div>
          <button
            onClick={handleBackToHome}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-green-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h2 className="text-xl font-bold">Campaign Content Saved!</h2>
            <p className="text-gray-600 mt-2">Your campaign responses have been saved successfully.</p>
          </div>
          <button
            onClick={handleBackToHome}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Campaign Content Form</h1>
            <p className="text-gray-600">Please provide detailed information about your campaign requirements.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {questions.map(question => (
              <QuestionInput
                key={question.id}
                question={question}
                value={answers[question.id] || ""}
                onChange={(value) => handleAnswerChange(question.id, value)}
                error={validationErrors[question.id]}
              />
            ))}

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error submitting form</h3>
                    <p className="text-sm text-red-700 mt-1">{submitError.message}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={handleBackToHome}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
              >
                Back to Campaigns
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`px-6 py-2 rounded text-white font-medium ${
                  submitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {submitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignForm;
